/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving drugs.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector, useDispatch } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/**
 * Enumeration used to determine the state of a request.
 */
import { RequestState, IRequestState } from '@ngt/request-utilities';


import { useState, useEffect } from 'react';

import { bindActionCreators } from 'redux';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/*
 * Used to register the logics so that they are included in the store. 
 */

import reducerRegistry from '../../reducerRegistry';

/*
 * Used to get access to the API types and requests
 */

import * as Dtos from '../../../api/Neon.Dtos';

import client from '../../../api/client';


/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

interface IInitialDrugState {
    drugs: Dtos.Drug[] | null;
    loadState: IRequestState<Dtos.ResponseStatus>;
};

export interface IDrugState {
    drugs: IInitialDrugState;
};

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const DrugInitialState: IInitialDrugState = {
    drugs: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useDrugsSelector: TypedUseSelectorHook<IDrugState> = useSelector;

export const DrugSelectors = {
    selectDrugs: (state: IDrugState) => state.drugs.drugs,
    selectLoadState: (state: IDrugState) => state.drugs.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class DrugReducer extends ImmerReducer<IInitialDrugState> {
    public load() {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public loadSuccess(drugs?: Dtos.Drug[]) {
        this.draftState.drugs = drugs ? drugs : null;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public loadFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.drugs = null;
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState = { ...DrugInitialState };
    }
};

export const DrugActions = createActionCreators(DrugReducer);
const reducer = createReducerFunction(DrugReducer, DrugInitialState);

/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const DrugApi = {
    load: () => {
        return client.get(new Dtos.GetDrugs());
    }
};

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const DrugLogic = {
    load: createLogic<IDrugState, {}, undefined, string, ReturnType<typeof DrugActions.load>>({
        type: DrugActions.load.type,
        process: async ({ action }, dispatch, done) => {
            try {
                const response = await DrugApi.load();

                dispatch(DrugActions.loadSuccess(
                    response.drugs
                ));
            }
            catch (error: any) {
                dispatch(DrugActions.loadFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    DrugLogic.load
];

/*
* ---------------------------------------------------------------------------------
* Hooks
* ---------------------------------------------------------------------------------
*/

export const useDrugs = (): [Dtos.Drug[] | null, IRequestState<Dtos.ResponseStatus>, typeof DrugActions] => {
    const drugs = useSelector(DrugSelectors.selectDrugs);
    const loadState = useSelector(DrugSelectors.selectLoadState);

    const dispatch = useDispatch();

    const [actions, setActions] = useState<typeof DrugActions>(() => {
        return bindActionCreators(DrugActions, dispatch);
    });

    useEffect(() => {
        setActions(bindActionCreators(DrugActions, dispatch));
    }, [dispatch, setActions]);

    return [
        drugs,
        loadState,
        actions
    ];
};

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('drugs', reducer as any, logics as any);